import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import { Box, type ITabItem, Status, Typography } from 'shared/ui'
import modalStore from 'shared/ui/Modal/store/modalStore'
import {
  ChatbotFollowUpAssertiveness,
  ChatbotMessageLength,
  ChatbotToneOfVoice,
  type IChatbotFollowUpAssertiveness,
  type IChatbotMessageLength,
  type IChatbotSettings,
  type IChatbotToneOfVoice,
} from 'entities/Chatbot'
import styles from 'pages/chatbot/pages/agentBookAppointment/ui/AdvancedSettings/styles.module.scss'
import { AdvancedSettingsModal } from 'pages/chatbot/pages/agentBookAppointment/ui/AdvancedSettings/AdvancedSettingsModal'
import { type AgentBookAppointmentStepsStore } from './agentBookAppointmentStepsStore'
import { PromptSettingsStore } from './promptSettingsStore'

export class AdvancedSettingsStore {
  static DefaultFollowUpWindow = 3
  static DefaultAutoFollowUpEnabled = true
  static DefaultFollowUpAssertiveness = ChatbotFollowUpAssertiveness.Steady
  static DefaultToneOfVoice = ChatbotToneOfVoice.Friendly
  static DefaultMessageLength = ChatbotMessageLength.Standard

  private _activeTabKey = 'general'
  private _followUpWindow = AdvancedSettingsStore.DefaultFollowUpWindow
  private _autoFollowUpEnabled = AdvancedSettingsStore.DefaultAutoFollowUpEnabled
  private _followUpAssertiveness: IChatbotFollowUpAssertiveness =
    AdvancedSettingsStore.DefaultFollowUpAssertiveness
  private _toneOfVoice: IChatbotToneOfVoice = AdvancedSettingsStore.DefaultToneOfVoice
  private _messageLength: IChatbotMessageLength = AdvancedSettingsStore.DefaultMessageLength
  private _availability = 'always'
  private _originData: IChatbotSettings | null = null
  private _promptSettingsStore: PromptSettingsStore
  private _modalId = nanoid()

  constructor(private readonly _agentBookAppointmentStepsStore: AgentBookAppointmentStepsStore) {
    this._promptSettingsStore = new PromptSettingsStore(this)

    makeAutoObservable(this)
  }

  init = (data: IChatbotSettings | null) => {
    this._followUpWindow =
      data?.followup_window?.value ?? AdvancedSettingsStore.DefaultFollowUpWindow
    this._autoFollowUpEnabled =
      data?.enable_followup?.value ?? AdvancedSettingsStore.DefaultAutoFollowUpEnabled
    this._followUpAssertiveness =
      data?.followup_assertiveness?.value ?? AdvancedSettingsStore.DefaultFollowUpAssertiveness
    this._toneOfVoice = data?.respond_tone?.value ?? AdvancedSettingsStore.DefaultToneOfVoice
    this._messageLength =
      data?.respond_message_length?.value ?? AdvancedSettingsStore.DefaultMessageLength
    this._originData = data
    this._promptSettingsStore.init(data)
  }

  get tabs(): ITabItem<'general' | 'prompt'>[] {
    return [
      {
        key: 'general',
        name: 'General settings',
      },
      {
        key: 'prompt',
        name: 'Prompt configuration',
        icon: this._promptSettingsStore.errorsMap.size > 0 ? 'alertTriangle' : undefined,
        iconProps: {
          color: 'var(--action-contained-destructive-primary-normal)',
        },
      },
    ]
  }

  get availabilities() {
    return [
      {
        id: 'always',
        customLabel: (
          <Typography
            variant={'body-md-regular'}
            ariaLabel={'always'}
            color={'var(--content-primary-secondary)'}
          >
            Always available to respond
          </Typography>
        ),
      },
      {
        id: 'custom',
        customLabel: (
          <Box display={'flex'} alignItems={'baseline'} gap={1}>
            <Typography variant={'body-md-regular'} ariaLabel={'custom'}>
              Custom schedule
            </Typography>
            <Status
              title={'Coming soon'}
              intent={'white_label'}
              emphasis={'high'}
              size={'label'}
              className={styles.comingSoonStatus}
            />
          </Box>
        ),
        disabled: true,
      },
    ]
  }

  get params() {
    return {
      followUpWindow: this._followUpWindow,
      autoFollowUpEnabled: this._autoFollowUpEnabled,
      followUpAssertiveness: this._followUpAssertiveness,
      toneOfVoice: this._toneOfVoice,
      messageLength: this._messageLength,
      instructions: this._promptSettingsStore.instructions,
    }
  }

  get payload() {
    return {
      advanced_general: {
        settings: {
          followup_window: {
            value: this._followUpWindow,
          },
          enable_followup: {
            value: this._autoFollowUpEnabled,
          },
          followup_assertiveness: {
            value: this._followUpAssertiveness,
          },
          respond_tone: {
            value: this._toneOfVoice,
          },
          respond_message_length: {
            value: this._messageLength,
          },
        },
      },
    }
  }

  get activeTabKey() {
    return this._activeTabKey
  }

  get followUpWindow() {
    return this._followUpWindow
  }

  get followUpAssertiveness() {
    return this._followUpAssertiveness
  }

  get toneOfVoice() {
    return this._toneOfVoice
  }

  get messageLength() {
    return this._messageLength
  }

  get availability() {
    return this._availability
  }

  get promptSettingsStore() {
    return this._promptSettingsStore
  }

  get agentInstruction() {
    return this._agentBookAppointmentStepsStore.agentInstruction
  }

  get autoFollowUpEnabled() {
    return this._autoFollowUpEnabled
  }

  onAutoFollowUpToggle = (value: boolean) => {
    this._autoFollowUpEnabled = value
  }

  setActiveTabKey = (key: string) => {
    this._activeTabKey = key
  }

  openSettingsModal = () => {
    modalStore.addModal({
      id: this._modalId,
      position: 'right',
      title: 'Settings',
      width: 540,
      ModalContent: AdvancedSettingsModal,
      ModalContentProps: {
        advancedSettingsStore: this,
        getEnhancePromptData: this._agentBookAppointmentStepsStore.chatbot.getEnhancePromptData,
        id: this._agentBookAppointmentStepsStore.chatbotId,
      },
      pureContent: true,
    })
  }

  setFollowUpWindow = (value: number) => {
    this._followUpWindow = value
  }

  setFollowUpAssertiveness = (value: IChatbotFollowUpAssertiveness) => {
    this._followUpAssertiveness = value
  }

  setToneOfVoice = (value: IChatbotToneOfVoice) => {
    this._toneOfVoice = value
  }

  setMessageLength = (value: IChatbotMessageLength) => {
    this._messageLength = value
  }

  setOriginData = (data: IChatbotSettings | null) => {
    this._originData = data
  }

  saveAndValidateSettings = () => {
    const isValid = this._promptSettingsStore.validate()
    if (!isValid) return false

    this._agentBookAppointmentStepsStore.setAgentData({
      ...this.payload,
      ...this._promptSettingsStore.payload,
    })
    return true
  }

  handleSaveSettings = () => {
    const isValid = this.saveAndValidateSettings()
    if (isValid) this.closeModal()
  }

  handleCancel = () => {
    this.reset()
    this._promptSettingsStore.reset()
    this.closeModal()
  }

  closeModal = () => {
    modalStore.closeModal(this._modalId)
  }

  reset = () => {
    this._followUpWindow =
      this._originData?.followup_window?.value ?? AdvancedSettingsStore.DefaultFollowUpWindow
    this._autoFollowUpEnabled =
      this._originData?.enable_followup?.value ?? AdvancedSettingsStore.DefaultAutoFollowUpEnabled
    this._followUpAssertiveness =
      this._originData?.followup_assertiveness?.value ??
      AdvancedSettingsStore.DefaultFollowUpAssertiveness
    this._toneOfVoice =
      this._originData?.respond_tone?.value ?? AdvancedSettingsStore.DefaultToneOfVoice
    this._messageLength =
      this._originData?.respond_message_length?.value ?? AdvancedSettingsStore.DefaultMessageLength
  }

  dispose = () => {
    this._promptSettingsStore.dispose()
  }
}
